<!--
@component
TeamPage

See markup below.
-->

<script lang="ts">
  import type { MatchesData } from '$lib/@types';

  import { appState } from '$lib/stores/index.svelte';
  import { Block } from '@reuters-graphics/graphics-components';
  import Flag from '$lib/components/common/Flag/index.svelte';
  import Squads from './Squads/index.svelte';
  import Matches from './Matches/index.svelte';
  import Trophies from './Trophies/index.svelte';

  /** Minimal team shape this page needs (from `getTeams`). */
  type Team = {
    id: string;
    code: string;
    name: string;
    slug: string;
    [key: string]: unknown;
  };

  /** Minimal squad member shape this page needs. */
  type SquadMember = {
    firstName?: string;
    lastName?: string;
    matchName?: string;
    role?: string;
    position?: string;
    shirtNumber?: string | number;
    [key: string]: unknown;
  };

  let {
    data,
    team,
    squad,
  }: {
    data: MatchesData;
    team: Team;
    squad: SquadMember[];
  } = $props();

  // Prefer the resolved `team` prop (from the route loader) and fall back to
  // the global `activeCountry` so the title still renders during in-flight
  // updates from the live data poller.
  let displayName = $derived(
    team?.name || appState.activeCountry[0]?.name || ''
  );
</script>

<!-- Hero Headline -->
<Block class="hero fmy-7" width="wide">
  <div
    class="hero-image relative text-center mx-auto fmb-5"
    class:loading={appState.isLoading}
  >
    <Flag
      country={team.slug}
      width="clamp(6.25rem, 10.42vw + 4.17rem, 12.5rem)"
    />
  </div>
  <div class="hero-title fmt-3 text-center">
    <h1 class="!text-3xl">{displayName}</h1>
  </div>
</Block>

<Trophies teamId={team.id} />

<!-- Players -->
{#if squad && squad.length > 0}
  <Block class="players fmy-7" width="widest">
    <Squads {squad} />
  </Block>
{/if}

<!-- Schedule and results -->
<Block class="schedule-results fmy-7" width="normal">
  <Matches {data} {team} />
</Block>

<style lang="scss">
  @use '../../styles/mixins' as mixins;

  .hero-image {
    @include mixins.soft-shadow;
    width: clamp(6.25rem, 10.42vw + 4.17rem, 12.5rem);
    aspect-ratio: 3/2;

    &.loading {
      // `Flag` ships the inlined base64 fallback PNG until the real flag
      // decodes; no need for another network `UNKNOWN.png` behind it.
      background-color: var(--theme-colour-tan-grey);
      &:before {
        content: '';
        position: absolute;
        z-index: 1;
        left: 0;
        top: 0;
        border-radius: var(--theme-style-border-radius);
        mix-blend-mode: multiply;
        width: 100%;
        height: 100%;
        @include mixins.loading;
      }
    }
  }
</style>
